/* eslint-disable tailwindcss/no-custom-classname */
import React, { useState, useEffect } from "react";
import Utils from "../../../utility";
import {
  getProjectDetails as fetchProjectDetails,
  updateProject
} from "../../../services/userService";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../../slices/userSlice";
import { Skeleton } from "../../../components/ui/skeleton";

const AiGeneratedPopup = ({
  setStep,
  setCurrentPopup,
  setProjectDetailState,
  projectDetailsState,
  projectDetailsGeneratedData,
  regenerateImages
}) => {
  const dispatch = useDispatch();
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageUrl, setSelectedImageUrl] = useState("");
  const [loadedImages, setLoadedImages] = useState([]);
  const currentUrl = window.location.href;
  const projectTemplateIds = currentUrl.split("/");
  const [projectDetails, setProjectDetails] = useState({});
  const projectDetailsResponse =
    projectDetailsGeneratedData?.promptRes?.images || projectDetails?.images;

  const getProjectDetails = async (projectId) => {
    try {
      const [error, response] = await Utils.parseResponse(
        fetchProjectDetails(projectId)
      );

      if (error || !response) {
        console.error("Error");
        return;
      }

      setProjectDetails(response?.responseData);
    } catch (err) {
      console.error("An error occurred while fetching project details:", err);
    } finally {
      //
    }
  };

  useEffect(() => {
    if (projectTemplateIds[7]?.includes("true")) {
      getProjectDetails(projectTemplateIds[8]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newImage =
      projectDetailsState?.promptRes?.images || projectDetails?.images;
    if (newImage && newImage.length > 0) {
      setSelectedImage(null);
      setSelectedImageUrl("");
      // Reset loaded images when new images arrive
      setLoadedImages([]);
    }
  }, [
    projectDetailsState?.promptRes?.images,
    projectDetailsGeneratedData.images,
    projectDetails?.images,
    projectDetailsGeneratedData?.promptRes?.images
  ]);

  // Progressive image loading effect
  useEffect(() => {
    const images = projectDetailsResponse || [];
    if (images.length === 0) return;

    // Simulate progressive loading - show images one by one
    images.forEach((imageUrl, index) => {
      setTimeout(() => {
        setLoadedImages((prev) => {
          if (prev.includes(imageUrl)) return prev;
          return [...prev, imageUrl];
        });
      }, index * 300); // 300ms delay between each image
    });
  }, [projectDetailsResponse]);
  const handleTheAIGeneratedImagesPopup = () => {
    localStorage.setItem("projectDetails", JSON.stringify(projectDetailsState));
    setCurrentPopup("");

    const projectId =
      projectDetailsGeneratedData?.promptRes?._id || projectTemplateIds[8];

    // gpt-image-2 produces a finished ad; skip the template editor and go
    // straight to the final review screen where the user can publish/download.
    if (projectId) {
      window.location.href = `/dashboard/brand/details/project/finalReview/${projectId}`;
    } else {
      setStep(4);
    }
  };

  const updateProjectDetailsById = async () => {
    try {
      dispatch(showLoader());
      const requestData = {
        selectedImage: selectedImageUrl
      };
      // eslint-disable-next-line no-unused-vars
      const [error, response] = await Utils.parseResponse(
        updateProject(
          projectDetailsGeneratedData?.promptRes?._id || projectTemplateIds[8],
          requestData
        )
      );
      if (error) {
        if (error?.response?.data?.message) {
          Utils.failureToastMessage(error?.response?.data?.message);
          return;
        } else {
          Utils.failureToastMessage("Some error occured");
          return;
        }
      } else {
        handleTheAIGeneratedImagesPopup();
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleImageClick = (index, url) => {
    setSelectedImage(index);
    setSelectedImageUrl(url);
    setProjectDetailState((prev) => ({
      ...prev,
      selectedImage: url
    }));
  };

  return (
    <div className="fixed right-0 top-0 z-50 flex size-full items-center justify-center bg-blurDark px-15 backdrop-blur-sm">
      <div className="flex max-h-90vh w-auto flex-col overflow-hidden rounded-15px bg-white">
        {/* Fixed Header */}
        <div className="shrink-0 px-20 pt-13">
          <h1 className="text-center font-InterBold text-ft32-41.6 text-black-9">
            Choose Your Ad
          </h1>
          <p className="mt-2.5 text-center font-InterRegular text-ft14-19.6 text-grey-14">
            Choose your favorite ad below. The selected design becomes your
            finished ad.
          </p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-20 py-10">
          <div className="grid grid-cols-2 gap-x-4.5 gap-y-6">
            {projectDetailsResponse?.length > 0
              ? projectDetailsResponse.map((image, index) => {
                  const isLoaded = loadedImages.includes(image);
                  return (
                    <div
                      key={index}
                      onClick={() => isLoaded && handleImageClick(index, image)}
                      className={`relative ${isLoaded ? "cursor-pointer" : ""}`}
                    >
                      {selectedImage === index && isLoaded && (
                        <div className="absolute left-2.5 top-2.5 z-10 flex size-10.2 items-center justify-center rounded-full bg-green-12">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="white"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="feather feather-check size-5"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </div>
                      )}

                      {!isLoaded ? (
                        <Skeleton
                          variant="pulse"
                          height="285px"
                          width="567px"
                          className="rounded-25px"
                        />
                      ) : (
                        <img
                          className={`h-285px w-567px rounded-25px object-cover transition-all ${
                            selectedImage === index
                              ? "border-2px border-green-12"
                              : ""
                          }`}
                          alt={`ai-images-${index}`}
                          src={image}
                        />
                      )}
                    </div>
                  );
                })
              : // Show skeleton placeholders while waiting for images
                [0, 1, 2, 3].map((index) => (
                  <div key={`skeleton-${index}`} className="relative">
                    <Skeleton
                      variant="pulse"
                      height="285px"
                      width="567px"
                      className="rounded-25px"
                    />
                  </div>
                ))}
          </div>
        </div>

        {/* Fixed Footer with Action Buttons */}
        <div className="border-gray-200 shrink-0 border-t px-20 pb-9 pt-5">
          <div className="flex items-center justify-center gap-8">
            {/* Regenerate and Next Buttons */}
            <button
              onClick={regenerateImages}
              className="hover:bg-gray-50 flex h-43px w-50 items-center justify-center rounded-15px border-2px border-grey-14 bg-white font-InterBold text-ft14-19.6 text-grey-14 shadow-buttonShadow transition-colors"
            >
              REGENERATE IMAGES
            </button>
            <button
              disabled={selectedImage === null}
              onClick={updateProjectDetailsById}
              className={`flex h-43px w-75 items-center justify-center rounded-15px bg-green-12 font-InterBold text-ft14-19.6 text-white shadow-buttonShadow transition-opacity ${
                selectedImage !== null
                  ? "hover:bg-green-11 opacity-100"
                  : "cursor-not-allowed opacity-50"
              }`}
            >
              NEXT
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiGeneratedPopup;
